/**
 * FEAT-25 — the three core anti-cheat checks a recruiter can switch off per
 * test: the camera check, fullscreen, and activity monitoring (tab switches,
 * paste, blocked shortcuts, cursor, second screen, network).
 *
 * The server freezes them at /start (`TestSnapshot.anticheat`) and drops any
 * event that belongs to a switched-off check, so everything the browser does
 * with this config is the candidate's EXPERIENCE — which steps appear, what is
 * blocked — never the security boundary.
 */
export interface AnticheatConfig {
  camera: boolean;
  fullscreen: boolean;
  activity: boolean;
}

export const ANTICHEAT_ALL_ON: AnticheatConfig = {
  camera: true,
  fullscreen: true,
  activity: true,
};

/**
 * Read a config off the wire or out of sessionStorage. A check is off ONLY on
 * an exact `false`: a missing field, a cached record written before FEAT-25
 * or plain garbage all read as on — which is what every such attempt is, and
 * the direction that can never quietly stop monitoring a monitored test.
 */
export function resolveAnticheat(raw: unknown): AnticheatConfig {
  if (typeof raw !== "object" || raw === null) return ANTICHEAT_ALL_ON;
  const r = raw as Record<string, unknown>;
  return {
    camera: r.camera !== false,
    fullscreen: r.fullscreen !== false,
    activity: r.activity !== false,
  };
}

/**
 * The checks a pre-start page may act on: `null` until /preview has answered.
 * The Welcome card renders while /preview loads, and reading "all on" there
 * advertised a webcam — and offered the camera check — on a test that has
 * none. Never guess a camera.
 */
export function checksFromPreview(
  preview: { anticheat?: unknown } | null | undefined,
): AnticheatConfig | null {
  return preview ? resolveAnticheat(preview.anticheat) : null;
}

/** True when the recruiter switched every check off for this test. */
export function anticheatAllOff(config: AnticheatConfig): boolean {
  return !config.camera && !config.fullscreen && !config.activity;
}
